import React, { useState, useEffect } from "react";

// Professional Image Carousel Component
export const ImageCarousel = ({ images, autoPlay = true, showDots = true, interval = 5000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (autoPlay && images.length > 1) {
      const timer = setInterval(() => {
        setCurrentIndex((prevIndex) => 
          prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
      }, interval);

      return () => clearInterval(timer);
    }
  }, [autoPlay, images.length, interval, currentIndex]);

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === images.length - 1 ? 0 : currentIndex + 1);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  if (!images || images.length === 0) {
    return <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center">
      <span className="text-gray-500">No images available</span>
    </div>;
  }

  return (
    <div className="relative w-full h-64 md:h-80 lg:h-96 overflow-hidden rounded-lg shadow-lg">
      {/* Images */}
      <div className="relative w-full h-full">
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-500 ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image.url}
              alt={image.alt || `Professional image ${index + 1}`}
              className="w-full h-full object-cover"
              loading={index === 0 ? "eager" : "lazy"}
            />
            {image.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                <p className="text-white text-sm font-medium">{image.caption}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition duration-300"
            aria-label="Previous image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition duration-300"
            aria-label="Next image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {showDots && images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition duration-300 ${
                index === currentIndex
                  ? 'bg-white'
                  : 'bg-white bg-opacity-50 hover:bg-opacity-75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Professional Image Grid Component
export const ImageGrid = ({ images, columns = 3, className = "" }) => {
  const gridCols = {
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-6 ${className}`}>
      {images.map((image, index) => (
        <div key={index} className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition duration-300">
          <img
            src={image.url}
            alt={image.alt || `Professional image ${index + 1}`}
            className="w-full h-48 object-cover group-hover:scale-105 transition duration-300"
            loading="lazy"
          />
          {image.title && (
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-100 transition duration-300">
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className="text-white font-semibold text-lg">{image.title}</h3>
                {image.description && (
                  <p className="text-white text-sm opacity-90 mt-1">{image.description}</p>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Single Professional Image Component
export const ProfessionalImage = ({ 
  src, 
  alt, 
  className = "", 
  caption = null,
  overlay = false,
  aspectRatio = "16/9" 
}) => {
  const aspectRatios = {
    "16/9": "aspect-video",
    "4/3": "aspect-4/3",
    "1/1": "aspect-square",
    "3/2": "aspect-3/2"
  };

  return (
    <div className={`relative overflow-hidden rounded-lg shadow-lg ${className}`}>
      <div className={`${aspectRatios[aspectRatio] || "aspect-video"}`}>
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {overlay && (
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-30"></div>
        )}
      </div>
      {caption && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
          <p className="text-white text-sm font-medium">{caption}</p>
        </div>
      )}
    </div>
  );
};

// Image Data Collections
export const professionalImages = {
  // Team and collaboration images
  team: [
    {
      url: "https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjB0ZWFtfGVufDB8fHx8MTc1NTY2Mjg0Nnww&ixlib=rb-4.1.0&q=85",
      alt: "Professional team member working at computer in modern office",
      caption: "Dedicated IT professionals delivering excellence"
    },
    {
      url: "https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxvZmZpY2UlMjB0ZWFtfGVufDB8fHx8MTc1NTY2Mjg0Nnww&ixlib=rb-4.1.0&q=85",
      alt: "Team collaboration meeting around conference table",
      caption: "Collaborative approach to problem solving"
    },
    {
      url: "https://images.unsplash.com/photo-1572021335469-31706a17aaef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxvZmZpY2UlMjB0ZWFtfGVufDB8fHx8MTc1NTY2Mjg0Nnww&ixlib=rb-4.1.0&q=85",
      alt: "Modern team working together with laptops and technology",
      caption: "Innovation through teamwork and technology"
    }
  ],

  // Business meeting images
  meetings: [
    {
      url: "https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzU1NjYyODUzfDA&ixlib=rb-4.1.0&q=85",
      alt: "Professional conference room meeting with team members",
      caption: "Strategic planning and client consultation"
    },
    {
      url: "https://images.unsplash.com/photo-1709715357520-5e1047a2b691?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzU1NjYyODUzfDA&ixlib=rb-4.1.0&q=85",
      alt: "Business meeting with laptops and presentations",
      caption: "Technology solutions planning and implementation"
    }
  ],

  // IT and technology images
  technology: [
    {
      url: "https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg",
      alt: "Modern data center with blue-lit servers",
      caption: "State-of-the-art data center infrastructure"
    },
    {
      url: "https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxJVCUyMGluZnJhc3RydWN0dXJlfGVufDB8fHx8MTc1NTY2Mjg5OXww&ixlib=rb-4.1.0&q=85",
      alt: "Professional computer motherboard close-up",
      caption: "Advanced IT infrastructure and hardware expertise"
    },
    {
      url: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxjeWJlcnNlY3VyaXR5fGVufDB8fHx8MTc1NTY2MjkwOHww&ixlib=rb-4.1.0&q=85",
      alt: "Cybersecurity circuit board with LED lights",
      caption: "Advanced cybersecurity technology solutions"
    }
  ],

  // Office environment images
  office: [
    {
      url: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzU1NjYyOTQ0fDA&ixlib=rb-4.1.0&q=85",
      alt: "Modern office boardroom with professional furniture",
      caption: "Professional meeting spaces for client collaboration"
    },
    {
      url: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzU1NjYyOTQ0fDA&ixlib=rb-4.1.0&q=85",
      alt: "Active workspace with people working in modern office",
      caption: "Dynamic work environment fostering innovation"
    }
  ],

  // Corporate building images
  corporate: [
    {
      url: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBidWlsZGluZ3xlbnwwfHx8fDE3NTU2NjI5NTB8MA&ixlib=rb-4.1.0&q=85",
      alt: "Corporate high-rise buildings from low angle",
      caption: "Professional corporate presence and growth"
    },
    {
      url: "https://images.unsplash.com/photo-1704423846283-f92ff6badea3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBidWlsZGluZ3xlbnwwfHx8fDE3NTU2NjI5NTB8MA&ixlib=rb-4.1.0&q=85",
      alt: "Modern corporate building with contemporary design",
      caption: "Establishing strong corporate foundations"
    }
  ]
};

export default { ImageCarousel, ImageGrid, ProfessionalImage, professionalImages };